<template>
  <v-card v-if="$vuetify.breakpoint.xs">
    <v-card-text>
      This feature is only supported on larger screen-sizes.
    </v-card-text>
  </v-card>
  <v-card 
    v-else
    class="pa-0"
    elevation="0"
    :disabled="disabled"
  >
    <api-errors :errors="errors" />
    <v-row no-gutters>
      <v-col 
        cols="8"
        class="pr-1"
      >
        <div>
          <floor-plan-canvas 
            ref="floorPlanCanvas"
            v-bind="$props"
            exclude-status
            @select-item="selectItem"
            @update="(target, index, replacement) => {
              $emit('input', target, index, replacement)
              if (target === 'shapes') selectedShape = replacement
              else if (target === 'labels') selectedLabel = replacement
              else if (target === 'icons') selectedIcon = replacement
              else selectedTable = replacement
            }"
            @remove-table="(index) => {tables.splice(index, 1)}"
            @add-table="tables.push($event)"
          />
        </div>
      </v-col>
      <v-col
        cols="4"
        class="pl-1"
      >
        <selected-element
          v-if="(selectedShape || {}).name != null"
          :value="selectedShape"
          :disabled="disabled"
          :canvas-height="canvasHeight"
          hide-change-floating-box-position
          @click:close="() => {
            $refs.floorPlanCanvas.clearTransformer()
            selectItem()
          }"
          @click:duplicate="addElement($event, 'newShapeDefault', 'shapes', 'basic-shape-')"
          @click:delete="(event) => {
            $emit('delete-element', event, 'shapes')
            selectedShape = undefined
          }"
        >
          <v-row no-gutters>
            <v-col
              cols="12"
              md="6"
              class="px-3 pt-3 d-flex justify-center"
            > 
              <linear-select 
                v-model="selectedShape.textScale"
                label="Text Size"
                :options="[
                  {icon: 'mdi-alpha-s', value: 1},
                  {icon: 'mdi-alpha-m', value: 2},
                  {icon: 'mdi-alpha-l', value: 3},
                ]"
              />
            </v-col>
            <v-col
              cols="12"
              md="6"
              class="px-3 pt-3 d-flex justify-center"
            >
              <text-color-selector 
                v-model="selectedShape.textFill" 
                label="Text Color"
              />
            </v-col>
          </v-row>
          <v-row 
            no-gutters
            class="justify-space-around pt-5"
          >
            <div>
              Color*
              <v-card
                class="ml-3 mt-3"
                :color="selectedShape.fill"
                height="50px"
                width="50px"
              />
            </div>
            <v-color-picker 
              :value="selectedShape.fill"
              class="elevation-1"
              mode="hexa"
              hide-canvas
              show-swatches
              hide-mode-switch
              hide-inputs
              swatches-max-height="200px"
              @input="setColor('fill', $event)"
            />
          </v-row>
          <v-row no-gutters>
            <v-col
              cols="12"
              md="6"
              class="px-3 pt-3 d-flex justify-center"
            > 
              <linear-select 
                v-model="selectedShape.strokeWidth"
                label="Border Size"
                :options="[
                  {icon: 'mdi-pen-off', value: 0},
                  {icon: 'mdi-alpha-s', value: 2},
                  {icon: 'mdi-alpha-m', value: 4},
                  {icon: 'mdi-alpha-l', value: 8},
                ]"
              >
                <template #option-1-content="{ option }">
                  <scaler
                    scaled
                    x-axis="1"
                    y-axis="1"
                  >
                    <v-icon :color="option.color || 'black'">
                      {{ option.icon }}
                    </v-icon>
                  </scaler>
                </template>
              </linear-select>
            </v-col>
            <v-col
              v-if="selectedShape.shape === 'BOX'"
              cols="12"
              md="6"
              class="px-3 pt-3 d-flex justify-center"
            >
              <linear-select 
                v-model="selectedShape.cornerRadius"
                label="Corner Rounding"
                :options="[
                  {icon: 'mdi-angle-right', value: 0},
                  {icon: 'mdi-size-s', value: 4},
                  {icon: 'mdi-size-m', value: 8},
                  {icon: 'mdi-size-l', value: 16},
                  {icon: 'mdi-size-xl', value: 24},
                  {icon: 'mdi-size-xxl', value: 34},
                ]"
              >
                <template #option-1-content="{ option }">
                  <rotator rotated>
                    <v-icon class="black--text">
                      {{ option.icon }}
                    </v-icon>
                  </rotator>
                </template>
              </linear-select>
            </v-col>
          </v-row>
          <v-expand-transition>
            <v-row 
              v-if="selectedShape.strokeWidth > 0"
              no-gutters
              class="justify-space-around pt-5"
            >
              <div>
                Border Color*
                <v-card
                  class="ml-3 mt-3"
                  :color="selectedShape.stroke"
                  height="50px"
                  width="50px"
                />
              </div>
              <v-color-picker 
                :value="selectedShape.stroke"
                class="elevation-1"
                mode="hexa"
                hide-canvas
                show-swatches
                hide-mode-switch
                hide-inputs
                swatches-max-height="200px"
                @input="setColor('stroke', $event)"
              />
            </v-row>
          </v-expand-transition>
          <template #shape>
            <div class="title">
              Basic Shapes
            </div>
            <v-row no-gutters>
              <v-col
                cols="12"
                sm="5"
                md="4"
                class="px-3 pt-3"
              >
                <table-shape-selectable-card 
                  :default-elevation="1"
                  :active-elevation="5"
                  class="rounded-pill grey lighten-4"
                  @click="selectedShape.shape = 'BOX'"
                >
                  <div class="d-flex justify-center">
                    <v-icon 
                      size="80px"
                      class="black--text"
                    >
                      mdi-square
                    </v-icon>
                  </div>
                </table-shape-selectable-card>
              </v-col>
              <v-col
                cols="12"
                sm="5"
                md="4"
                class="px-3 pt-3"
              >
                <table-shape-selectable-card 
                  :default-elevation="1"
                  :active-elevation="5"
                  class="rounded-pill grey lighten-4"
                  @click="selectedShape.shape = 'CIRCLE'"
                >
                  <div class="d-flex justify-center">
                    <v-icon 
                      size="80px"
                      class="black--text"
                    >
                      mdi-circle
                    </v-icon>
                  </div>
                </table-shape-selectable-card>
              </v-col>
            </v-row>
          </template>
        </selected-element>
        <selected-element
          v-else-if="(selectedLabel || {}).name != null"
          :value="selectedLabel"
          :disabled="disabled"
          :canvas-height="canvasHeight"
          hide-change-floating-box-position
          hide-change-shape
          @click:close="() => {
            $refs.floorPlanCanvas.clearTransformer()
            selectItem()
          }"
          @click:duplicate="addElement($event, 'newLabelDefault', 'labels', 'label-')"
          @click:delete="(event) => {
            $emit('delete-element', event, 'labels')
            selectedLabel = undefined
          }"
        >
          <v-row no-gutters>
            <v-col
              cols="12"
              md="6"
              class="px-3 pt-3"
            >
              <v-autocomplete
                v-model="selectedLabel.fontFamily"
                :items="fontsList"
                label="Font"
                clearable
                persistent-hint
                hint="Some fonts may not display correctly on all devices"
                :disabled="disabled"
              >
                <template #item="{ item }">
                  <div :style="`font-family: ${item.value}`">
                    {{ item.text }}
                  </div>
                </template>
              </v-autocomplete>
            </v-col>
            <v-col
              cols="12"
              md="6"
              class="px-3 pt-3 d-flex justify-center"
            >
              <text-color-selector
                v-model="selectedLabel.fill"
                label="Label Color"
              />
            </v-col>
          </v-row>
        </selected-element>
        <selected-element
          v-else-if="(selectedIcon || {}).name != null"
          :value="selectedIcon"
          :disabled="disabled"
          :canvas-height="canvasHeight"
          hide-change-floating-box-position
          @click:close="() => {
            $refs.floorPlanCanvas.clearTransformer()
            selectItem()
          }"
          @click:duplicate="addElement($event, 'newIconDefault', 'icons', 'icon-')"
          @click:delete="(event) => {
            $emit('delete-element', event, 'icons')
            selectedIcon = undefined
          }"
        >
          <template #title>
            <scaler 
              scaled
              x-axis="0.8"
              y-axis="0.8"
            >
              <text-color-selector v-model="selectedIcon.color" />
            </scaler>
          </template>
          <template #element-text-field>
            <div id="icon-display-field">
              <v-text-field 
                :label="iconNamesMap[`${selectedIcon.icon}.png`] || arrowIconNamesMap[`${selectedIcon.icon}.png`]"
                disabled
                rounded
                outlined
                dense
                hide-details="always"
                :background-color="selectedIcon.color === 'white' ? 'grey lighten-1' : ''"
              >
                <template #append>
                  <v-fade-transition hide-on-leave>
                    <img 
                      :src="(customIconsMap[`${selectedIcon.color}-${selectedIcon.icon}.png`]
                        || arrowIconsMap[`${selectedIcon.color}-${selectedIcon.icon}.png`]
                        || {}
                      ).src"
                      style="height: 24px; width: auto;"
                    >
                  </v-fade-transition>
                </template>
              </v-text-field>
            </div>
          </template>
          <template #shape>
            <v-row no-gutters>
              <v-col 
                v-for="(icon, index) in iconsList"
                :key="`custom-icon-${index}`"
                cols="3"
                md="2"
                class="px-3 pt-3"
              >
                <table-shape-selectable-card 
                  :default-elevation="1"
                  :active-elevation="5"
                  style="transition: background-color 0.2s ease-in-out;"
                  class="d-flex justify-center grey lighten-4 rounded-xl"
                  :class="{'darken-3': selectedIcon.color === 'white'}"
                  @click="selectedIcon.icon = icon.value.replace('.png', '')"
                >
                  <v-fade-transition hide-on-leave>
                    <img 
                      :src="(customIconsMap[`${selectedIcon.color}-${icon.value}`] || {}).src"
                      class="py-2"
                    >
                  </v-fade-transition>
                </table-shape-selectable-card>
              </v-col>
            </v-row>
            <div class="title">
              Arrows
            </div>
            <v-row no-gutters>
              <v-col
                v-for="(icon, index) in arrowIconsList"
                :key="`arrow-icon-${index}`"
                cols="3"
                md="2"
                class="px-3 pt-3"
              >
                <table-shape-selectable-card
                  :default-elevation="1"
                  :active-elevation="5"
                  style="transition: background-color 0.2s ease-in-out;"
                  class="d-flex justify-center grey lighten-4 rounded-xl"
                  :class="{'darken-3': selectedIcon.color === 'white'}"
                  @click="selectedIcon.icon = icon.value.replace('.png', '')"
                >
                  <v-fade-transition hide-on-leave> 
                    <img
                      :src="(arrowIconsMap[`${selectedIcon.color}-${icon.value}`] || {}).src"
                    >
                  </v-fade-transition>
                </table-shape-selectable-card>
              </v-col>
            </v-row>
          </template>
        </selected-element>
        <selected-element 
          v-else-if="(selectedTable || {}).name != null"
          :value="selectedTable"
          :disabled="disabled"
          :canvas-height="canvasHeight"
          @click:close="() => {
            $refs.floorPlanCanvas.clearTransformer()
            selectItem()
          }"
          @click:duplicate="addElement"
          @click:delete="(event) => {
            $emit('delete-element', event)
            selectedTable = undefined
          }"
        >
          <template #element-text-field>
            <v-text-field
              v-model="selectedTable.tableName"
              label="Table Name"
              :disabled="disabled"
              clearable
            />
          </template>
          <v-row no-gutters>
            <v-col
              cols="12"
              class="px-3 pt-3"
            >
              <v-combobox 
                v-model="selectedTable.group"
                label="Group Name"
                :items="joinGroupOptions"
                clearable
                auto
                :disabled="disabled"
              />
            </v-col>
          </v-row>
          <v-row no-gutters>
            <v-col
              cols="12"
              md="6"
              class="px-3"
            >
              <translator y-axis="15px">
                <span class="caption text--secondary pl-3">Min Covers *</span>
              </translator>
              <number-field
                v-model="selectedTable.minCovers"
                :placeholder="notValidNumber(selectedTable.definitionMinCovers) ? null : selectedTable.definitionMinCovers.toString()"
                :rules="[...rules().numeric(1), ...rules().decimals(0)]"
                :disabled="disabled"
              />
            </v-col>
            <v-col
              cols="12"
              md="6"
              class="px-3"
            >
              <translator y-axis="15px">
                <span class="caption text--secondary pl-3">Max Covers *</span>
              </translator>
              <number-field
                v-model="selectedTable.maxCovers"
                :placeholder="notValidNumber(selectedTable.definitionMaxCovers) ? null : selectedTable.definitionMaxCovers.toString()"
                :rules="[...rules().numeric(selectedTable.minParties), ...rules().decimals(0)]"
                :disabled="disabled"
              />
            </v-col>
          </v-row>
          <v-row no-gutters>
            <v-col
              cols="12"
              class="px-3"
            >
              <translator y-axis="15px">
                <span class="caption text--secondary pl-3">Max Join Covers *</span>
              </translator>
              <number-field
                v-model="selectedTable.maxJoinCovers"
                :placeholder="notValidNumber(selectedTable.definitionMaxJoinCovers) ? null : selectedTable.definitionMaxJoinCovers.toString()"
                :rules="[...rules().numeric(0), ...rules().decimals(0)]"
                :disabled="disabled"
              />
            </v-col>
          </v-row>
          <v-row no-gutters>
            <v-col
              cols="12"
              md="6"
              class="px-3"
            >
              <translator y-axis="15px">
                <span class="caption text--secondary pl-3">Quantity *</span>
              </translator>
              <number-field
                v-model="selectedTable.quantity"
                :placeholder="notValidNumber(selectedTable.definitionQuantity) ? null : selectedTable.definitionQuantity.toString()"
                :rules="[...rules().numeric(0), ...rules().decimals(0), ...rules().required('Quantity')]"
                disabled
              />
            </v-col>
            <v-col
              cols="12"
              md="6"
              class="px-3 pt-3"
            >
              <number-field
                v-model="selectedTable.autoAssignQuantity"
                label="Auto Assign Quantity"
                :rules="[...rules().numeric(0), ...rules().decimals(0)]"
                :disabled="disabled"
              />
            </v-col>
          </v-row>
          <template #shape>
            <v-row no-gutters>
              <v-col
                v-for="tableShape in tableImages"
                :key="'table-shape-' + tableShape.value"
                cols="12"
                sm="6"
                md="4"
                class="px-3 pt-3"
              >
                <table-shape-selectable-card @click="selectedTable.shape = tableShape.value">
                  <img 
                    :src="(tableImagesMap[tableShape.value] || {}).src" 
                    style="height: auto; width: 100%;"
                  >
                </table-shape-selectable-card>
              </v-col>
            </v-row>
          </template>
          <template #floating-box>
            <v-row no-gutters>
              <v-col
                v-for="floatingBoxPosition of floatingBoxPositions"
                :key="'floating-box-position-' + floatingBoxPosition.value"
                cols="12"
                sm="6"
                md="4"
                class="px-3 pt-3"
              >
                <v-hover v-slot="{ hover }">
                  <v-card 
                    :elevation="hover ? 10 : 1"
                    height="200px"
                    class="d-flex justify-center"
                    @click="selectedTable.floatingBoxPosition = floatingBoxPosition.value"
                  >
                    <div class="d-flex align-self-center font-weight-bold">
                      {{ floatingBoxPosition.text }}
                    </div>
                  </v-card>
                </v-hover>
              </v-col>
            </v-row>
          </template>
        </selected-element>
        <v-card
          v-else
          :height="(canvasHeight || 500) + 'px'"
        >
          <v-card-text class="font-weight-bold black--text">
            <v-menu offset-y>
              <template #activator="{ on, attrs }">
                <v-btn
                  v-bind="attrs"
                  v-on="on"
                >
                  <v-icon small>
                    mdi-plus
                  </v-icon>
                  Add Element
                </v-btn>
              </template>
              <v-list>
                <v-list-item @click="addElement(null)">
                  <v-list-item-icon class="mr-2">
                    <img 
                      style="height: 25px; width: auto;"
                      :src="(tableImagesMap['SQUARE_4'] || {}).src"
                    >
                  </v-list-item-icon>
                  <v-list-item-content class="font-weight-bold">
                    Table
                  </v-list-item-content>
                </v-list-item>
                <v-divider />
                <v-list-item @click="addShape('CIRCLE')">
                  <v-list-item-icon class="mr-2">
                    <v-icon>mdi-circle</v-icon>
                  </v-list-item-icon>
                  <v-list-item-content class="font-weight-bold">
                    Circle
                  </v-list-item-content>
                </v-list-item>
                <v-divider />
                <v-list-item @click="addShape('BOX')">
                  <v-list-item-icon class="mr-2">
                    <v-icon>mdi-square</v-icon>
                  </v-list-item-icon>
                  <v-list-item-content class="font-weight-bold">
                    Square
                  </v-list-item-content>
                </v-list-item>
                <v-divider />
                <v-list-item @click="addLabel()">
                  <v-list-item-icon class="mr-2">
                    <v-icon>mdi-format-textbox</v-icon>
                  </v-list-item-icon>
                  <v-list-item-content class="font-weight-bold">
                    Label
                  </v-list-item-content>
                </v-list-item>
                <v-divider />
                <v-list-item @click="addIcon()">
                  <v-list-item-icon class="mr-2">
                    <v-icon>mdi-star</v-icon>
                  </v-list-item-icon>
                  <v-list-item-content class="font-weight-bold">
                    Icon
                  </v-list-item-content>
                </v-list-item>
              </v-list>
            </v-menu>
            <v-divider class="my-3 grey" />
            <v-row no-gutters>
              <v-col
                cols="12"
                class="px-3 pt-3 d-flex justify-center"
              >
                <scaler
                  scaled
                  x-axis="1.3"
                  y-axis="1.3"
                >
                  <linear-select 
                    :value="gridDensity"
                    label="Grid Density"
                    :options="gridDensityOptions"
                    @input="$emit('update-field', 'gridDensity', $event)"
                  >
                    <template
                      v-for="option of gridDensityOptions"
                      #[`option-${option.value}-content`]
                    >
                      <span 
                        :key="`density-option-${option.value}`"
                        class="font-weight-bold"
                      >
                        {{ option.value }}
                      </span>
                    </template>
                  </linear-select>
                </scaler>
              </v-col>
            </v-row>
            <v-row no-gutters>
              <v-col
                align-self="center"
                cols="12"
                md="6"
                class="px-3 pt-3 d-flex justify-center"
              >
                <scaler
                  scaled
                  x-axis="1.2"
                  y-axis="1.2"
                >
                  <linear-select
                    :value="canvasWidth"
                    label="Canvas Width"
                    :options="canvasWidthOptions"
                    @input="$emit('update-field', 'canvasWidth', $event)"
                  >
                    <template
                      v-for="(width, widthIndex) of canvasWidthOptions"
                      #[`option-${width.index}-content`]
                    >
                      <span 
                        :key="`width-option-${widthIndex}`"
                        class="font-weight-bold"
                      >
                        {{ width.value / 100 }}
                      </span>
                    </template>
                  </linear-select>
                </scaler>
              </v-col>
              <v-col
                cols="12"
                md="6"
                class="px-3 pt-3 d-flex justify-center"
              >
                <scaler
                  scaled
                  x-axis="1.2"
                  y-axis="1.2"
                >
                  <linear-select
                    :value="canvasHeight"
                    label="Canvas Height"
                    :options="canvasHeightOptions"
                    @input="$emit('update-field', 'canvasHeight', $event)"
                  >
                    <template
                      v-for="(height, heightIndex) of canvasHeightOptions"
                      #[`option-${height.index}-content`]
                    >
                      <span 
                        :key="`height-option-${heightIndex}`"
                        class="font-weight-bold"
                      >
                        {{ height.value / 100 }}
                      </span>
                    </template>
                  </linear-select>
                </scaler>
              </v-col>
            </v-row>
            <v-row 
              no-gutters
              class="justify-center"
            >
              <v-switch 
                :input-value="showServerAssignment"
                label="Always Show Server Assignments"
                @change="$emit('update-field', 'showServerAssignment', $event)"
              />
            </v-row>
            <!-- <v-row 
              no-gutters
              class="justify-center"
            >
              <v-switch 
                :input-value="autoClearReservationStatus"
                label="Auto-Clear Reservation Status"
                @change="$emit('update-field', 'autoClearReservationStatus', $event)"
              />
            </v-row> -->
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
  </v-card>
</template>

<script>
import errorHandler from '@/ft-vue-core/mixins/errorHandler'
import ApiErrors from '@/ft-vue-core/components/ApiErrors'
import FloorPlanCanvas from './FloorPlanCanvas'
import rules from '@/ft-vue-core/mixins/rules'
import canvasHelpers from './mixins/canvasHelpers'
import TableShapeSelectableCard from './TableShapeSelectableCard'
import SelectedElement from './SelectedElement'
import TextColorSelector from './selectedElement/TextColorSelector'
import LinearSelect from '@/ft-vue-core/components/LinearSelect'
import Scaler from '@/ft-vue-core/components/transformers/Scaler'
import Rotator from '@/ft-vue-core/components/transformers/Rotator'
import Translator from '@/ft-vue-core/components/transformers/Translator'
export default {
  name: 'FloorPlanConfigurator',
  components: {ApiErrors, FloorPlanCanvas, TableShapeSelectableCard, SelectedElement, TextColorSelector, LinearSelect, Scaler, Rotator, Translator},
  mixins: [errorHandler, canvasHelpers, rules],
  props: {
    tables: {
      type: Array,
      default () {return []}
    },
    shapes: {
      type: Array,
      default () {return []}
    },
    labels: {
      type: Array,
      default () {return []}
    },
    icons: {
      type: Array,
      default () {return []}
    },
    newTableDefault: {
      type: Object,
      default () {return {}}
    },
    newShapeDefault: {
      type: Object,
      default () {return {}}
    },
    newLabelDefault: {
      type: Object,
      default () {return {}}
    },
    newIconDefault: {
      type: Object,
      default () {return {}}
    },
    gridDensity: Number,
    disabled: Boolean,
    joinGroupOptions: {
      type: Array,
      default () {return []}
    },
    canvasHeight: {
      type: Number,
      default: 500
    },
    canvasWidth: {
      type: Number,
      default: 1000
    },
    showServerAssignment: Boolean,
    autoClearReservationStatus: Boolean,
    locationId: Number,
  },
  data () {
    return {
      floorPlan: {},
      selectRect: false,
      selectedTable: undefined,
      selectedShape: undefined,
      selectedLabel: undefined,
      selectedIcon: undefined,
    }
  },
  computed: {
    canvasWidthOptions () {
      let result = []
      for (let i = 5; i <= 30; i++) result.push({value: i * 100, index: i - 4})
      return result
    },
    canvasHeightOptions () {
      let result = []
      for (let i = 4; i <= 20; i++) result.push({value: i * 100, index: i - 3})
      return result
    },
    gridDensityOptions () {
      let result = []
      for (let i = 1; i <= 5; i++) result.push({value: i})
      return result
    },
    selectedTab () {
      if (this.showChangeShape) return 1
      else if (this.showChangeFloatingBoxPosition) return 2
      return 0
    },
    xAxisMap () {
      return this.tables?.reduce((acc, t) => {
        acc[t.x] = t
        return acc
      }, {}) ?? {}
    },
    yAxisMap () {
      return this.tables?.reduce((acc, t) => {
        acc[t.y] = t
        return acc
      }, {}) ?? {}
    },
  },
  methods: {
    notValidNumber (value) {
      return isNaN(value) || value === null
    },
    setColor (field, value) {
      let computedValue = value
      if (typeof value === 'object') computedValue = value.hex
      this.$set(this.selectedShape, field, computedValue)
    },
    selectItem (target, index) {
      const dataTarget = {tables: 'selectedTable', shapes: 'selectedShape', labels: 'selectedLabel', icons: 'selectedIcon'}
      for (let key in dataTarget) this[dataTarget[key]] = undefined
      this.$nextTick(() => this.$set(this, dataTarget[target], this[target]?.[index] || {}))
    },
    setZIndex (index, replacement) {
      this.tables.splice(index, 1)
      this.tables.push(replacement)
    },
    addIcon () {
      this.addElement(null, 'newIconDefault', 'icons', 'icon-')
    },
    addLabel () {
      this.addElement(null, 'newLabelDefault', 'labels', 'label-')
    },
    addShape (shape) {
      this.addElement(null, 'newShapeDefault', 'shapes', 'basic-shape-', shape)
    },
    addElement (table, defaultLocation = 'newTableDefault', listLocation = 'tables', newNamePrefix, shapeEnum) {
      let x = Math.round(this.CANVAS_WIDTH / 2) - 50
      let y = Math.round(this.CANVAS_HEIGHT / 2) - 50
      while (this.xAxisMap[x] && this.yAxisMap[y]) x += 20, y += 20
      if (x + 100 > this.CANVAS_WIDTH || y + 100 > this.CANVAS_HEIGHT) return
      let newElement
      if (table) {
        if (!table.definitionDisabled) {
          table.definitionDisabled = true
          table.minCovers = table.definitionMinCovers
          table.definitionMinCovers = null
          table.maxCovers = table.definitionMaxCovers
          table.definitionMaxCovers = null
          table.quantity = table.definitionQuantity
          table.definitionQuantity = null
          table.maxJoinCovers = table.definitionMaxJoinCovers
          table.definitionMaxJoinCovers = null
        }
        newElement = {
          ...table,
          id: null,
          name: this.getUniqueName(listLocation, newNamePrefix),
          tableId: null,
          tableName: null,
          disabled: false,
          _localAddition: true, 
          x, y
        }
      }
      else newElement = {...this[defaultLocation], x, y, width: 100, height: 100}
      if (shapeEnum) newElement.shape = shapeEnum
      this.$emit('add-element', listLocation, newElement)
      // this[listLocation]?.push(newElement)
      this.$nextTick(() => {
        this.$refs.floorPlanCanvas?.setTransformer({tableName: newElement.name})
        if (listLocation === 'shapes') this.selectedShape = newElement
        else if (listLocation === 'labels') this.selectedLabel = newElement
        else if (listLocation === 'icons') this.selectedIcon = newElement
        else this.selectedTable = newElement
      })
    },
    getUniqueName (listLocation = 'tables', prefix = 'table-') {
      let max = 0
      for (let element of this[listLocation] ?? []) {
        const split = element.name?.split('-') ?? '0'
        const number = parseInt(split[split.length - 1])
        if (number > max) max = number
      }
      return prefix + ++max
    }
  }
}
</script>

<style scoped>
.cursor-pointer {
  cursor: pointer;
}
#icon-display-field .v-text-field >>> .v-input__control .v-input__slot {
  transition: background-color 0.2s ease-in-out;
}
</style>
